import React, { useState, useEffect } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { TextField } from '@mui/material';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const CreateFolderModal = ({ isOpen, onClose, onSuccess, mode = 'create', folder = null }) => {
  const { mode: themeMode } = useThemeMode();
  const [folderName, setFolderName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showFormLoading, setShowFormLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && folder) {
        setFolderName(folder.name);
      } else {
        setFolderName('');
      }
      setError('');
      setShowFormLoading(false);
      setShowValidation(false);
    }
  }, [isOpen, mode, folder]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);
    
    if (!folderName.trim()) {
      setError('Folder name is required');
      return;
    }

    setIsLoading(true);
    setShowFormLoading(true);
    setError('');

    try {
      let response;
      
      if (mode === 'edit' && folder) {
        response = await dataAPI.editFolder(folder.id, { name: folderName.trim() }, { showLoading: false });
      } else {
        response = await dataAPI.createFolder({ name: folderName.trim() }, { showLoading: false });
      }

      if (response.status === 'success') {
        onSuccess();
        onClose();
      } else {
        setError(response.message || 'Failed to save folder');
      }
    } catch (error) {
      console.error('Error saving folder:', error);
      setError('An error occurred while saving the folder');
    } finally {
      setIsLoading(false);
      setShowFormLoading(false);
    }
  };

  if (!isOpen) return null;

  const isEditMode = mode === 'edit';
  const title = isEditMode ? 'Rename Folder' : 'Create Folder';
  const buttonText = isEditMode ? 'Rename' : 'Create';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: themeMode === 'dark' ? '#1A1A1A' : '#ffffff',
        borderRadius: '20px',
        padding: '40px',
        width: '480px',
        maxWidth: '90vw',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px'
        }}>
          <h2 style={{
            color: themeMode === 'dark' ? '#FFFFFF' : '#222222',
            fontSize: '28px',
            margin: 0,
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: themeMode === 'dark' ? '#FFFFFF' : '#666666',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Form Loading Overlay */}
        {showFormLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1003,
            backgroundColor: themeMode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '20px'
          }}>
            {/* Fill Animation Loading Indicator */}
            <div className="fill-animation-container">
              {/* Background container */}
              <div className="fill-animation-background" />
              
              {/* Progress fill animation */}
              <div className="fill-animation-progress" />
              
              {/* AppIconBlue with pulse animation */}
              <img 
                src={AppIconBlue} 
                alt="Loading" 
                className={`fill-animation-icon pulse-animation-icon`}
              />
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <TextField
            size="small"
            required
            label="Folder Name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            disabled={isLoading}
            error={showValidation && !folderName.trim()}
            helperText={showValidation && !folderName.trim() ? 'Folder name is required' : ''}
            style={{
              width: '100%',
              backgroundColor: themeMode === 'dark' ? '#1a1a1a' : '#ffffff',
              color: themeMode === 'dark' ? '#FFFFFF' : '#222222'
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                height: '56px',
                '& fieldset': {
                  borderColor: themeMode === 'dark' ? '#444' : '#ddd',
                },
                '&:hover fieldset': {
                  borderColor: '#26C3C0',
                  boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#26C3C0',
                  boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                },
                '&.Mui-error fieldset': {
                  borderColor: '#f44336',
                },
              },
              '& .MuiInputLabel-root': {
                color: themeMode === 'dark' ? '#999' : '#666',
                fontSize: '16px',
              },
              '& .MuiFormHelperText-root': {
                color: '#f44336',
                fontSize: '14px',
              },
            }}
          />

          {/* Error Message */}
          {error && (
            <div style={{
              color: '#ff6b6b',
              fontSize: '14px',
              marginTop: '8px',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !folderName.trim()}
            style={{
              width: '100%',
              height: '56px',
              marginTop: '24px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: isLoading || !folderName.trim() 
                ? (themeMode === 'dark' ? '#666666' : '#cccccc')
                : '#26C3C0',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: '500',
              fontFamily: 'Urbanist, Arial, sans-serif',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!isLoading && folderName.trim()) {
                e.target.style.backgroundColor = '#22B0AD';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading && folderName.trim()) {
                e.target.style.backgroundColor = isLoading || !folderName.trim() 
                  ? (themeMode === 'dark' ? '#666666' : '#cccccc')
                  : '#26C3C0';
              }
            }}
          >
            {isLoading ? 'Saving...' : buttonText}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateFolderModal;
